import { Caption } from '../caption'
import { FieldShell } from '../FieldShell'
import { useFieldContext, type FieldComponentProps } from '../rendererContext'
import { isBooleanToggle, type BooleanConfig } from '../types'
import { useControlMeta } from './useControlMeta'

export function BooleanField({ field, path, value, errors }: FieldComponentProps) {
  const meta = useControlMeta(field, path, errors)
  const config = field.config as BooleanConfig | undefined
  const checked = value === true
  // Only a toggle can be unanswered — a checkbox seeds `false`, and unticked is
  // its answer. See `isBooleanToggle`.
  const unanswered = value !== true && value !== false && isBooleanToggle(field)

  if (field.display_only) {
    return (
      <FieldShell field={field} path={path} errors={errors}>
        <div id={meta.id} className="form-control-plaintext">
          {/* Blank, not "No" — read-only text is the only signal here, so an
              unanswered question must not read as a deliberate answer. */}
          {unanswered ? '' : checked ? 'Yes' : 'No'}
        </div>
      </FieldShell>
    )
  }

  // render_as: "buttons" → a two-button toggle group; otherwise a checkbox.
  if (config?.render_as === 'buttons') {
    // Neither button is pressed until the user answers: keying "No" off
    // `!checked` used to render it as chosen from the start.
    const toggle = (label: string, option: boolean) => {
      const selected = !unanswered && checked === option
      return (
        <button
          type="button"
          className={`btn ${selected ? 'btn-primary' : 'btn-outline-primary'}`}
          aria-pressed={selected}
          disabled={meta.disabled}
          onClick={() => {
            meta.setFieldValue(path, option)
            meta.notifyBlur(path)
          }}
        >
          {label}
        </button>
      )
    }
    return (
      <FieldShell field={field} path={path} errors={errors} groupLabel>
        <div
          className="btn-group"
          role="group"
          id={meta.id}
          aria-labelledby={meta.labelId}
          aria-describedby={meta.describedBy}
        >
          {toggle('Yes', true)}
          {toggle('No', false)}
        </div>
      </FieldShell>
    )
  }

  // Checkbox markup matches the RX form builder / forms-universal pattern: the
  // input is *nested inside* its <label> (with the text in a sibling <span>), so
  // the app's custom checkbox styling (`label input[type=checkbox]`) applies.
  const containerClass = meta.classNames.checkContainer ?? 'checkbox'
  const labelClass = `${meta.classNames.checkLabel ?? 'label--flex'}${meta.disabled ? ' label--disabled' : ''}`
  // FieldShell's label is hidden here (the text lives in the sibling <span>),
  // so labelSuffix — the host's per-field marker (e.g. a "Double-check" pill)
  // — is rendered inline next to the label text instead.
  const { labelSuffix } = useFieldContext()
  const labelSuffixNode = labelSuffix?.(path) ?? null

  return (
    <FieldShell field={field} path={path} errors={errors} hideLabel>
      <div className={containerClass}>
        <label className={labelClass}>
          <input
            id={meta.id}
            type="checkbox"
            className={meta.hasErrors ? 'is-invalid' : undefined}
            checked={checked}
            disabled={meta.disabled}
            aria-invalid={meta.ariaInvalid}
            aria-required={meta.ariaRequired}
            aria-describedby={meta.describedBy}
            onChange={(event) => {
              meta.setFieldValue(path, event.target.checked)
              meta.notifyBlur(path)
            }}
          />
          {field.label ? (
            <span>
              <Caption field={field} text={field.label} />
              {field.required ? (
                <>
                  <abbr className="text-danger ms-1" title="required" aria-hidden="true">
                    *
                  </abbr>
                  <span className="visually-hidden"> (required)</span>
                </>
              ) : null}
              {labelSuffixNode}
            </span>
          ) : null}
        </label>
      </div>
    </FieldShell>
  )
}
